import { Banknote, Download, Eye, Filter, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  InlineMessage,
  PageHeader,
  SearchField,
  StatusChip,
} from "../../components/ui";
import { useApp } from "../../domain/AppContext";
import { compareClassNames, formatClassCodesInText, formatClassName } from "../../domain/classCode";
import { isStudentEligible } from "../../domain/rules";
import type {
  Enrollment,
  EnrollmentPayment,
  EnrollmentStatus,
  EnrollmentAnswers,
} from "../../domain/types";

import { remoteRepository } from "../../services/remoteRepository";

type PaymentFilter = "all" | Enrollment["paymentStatus"];
const money = (amount: number) =>
  amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const paymentLabel = (
  status: Enrollment["paymentStatus"] | EnrollmentPayment["status"],
) =>
  status === "paid"
    ? "Pago"
    : status === "pending"
      ? "Pendente"
      : status === "invalidated"
        ? "Invalidado"
        : "Não aplicável";

export function EnrollmentsPage() {
  const { profile, data, cancelEnrollment, updateEnrollmentPayment, createEnrollmentForStudents, notify } = useApp();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"all" | EnrollmentStatus>("all");
  const [payment, setPayment] = useState<PaymentFilter>("all");
  const [olympiadId, setOlympiadId] = useState("all");
  const [className, setClassName] = useState("all");
  const [active, setActive] = useState<Enrollment | null>(null);
  const [working, setWorking] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createOlympiadId, setCreateOlympiadId] = useState("");
  const [createClass, setCreateClass] = useState("all");
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [teamName, setTeamName] = useState("");
  useEffect(() => {
    if (active && data) {
      const persisted = data.enrollments.find((item) => item.id === active.id);
      if (persisted) setActive(persisted);
    }
  }, [data]);
  if (!data) return null;
  const isStudent = profile?.role === 'student';
  const canEnroll = Boolean(profile && (profile.permissions.includes("*") || profile.permissions.includes("enrollments:create")));
  const canPay = Boolean(profile && (profile.permissions.includes("*") || profile.permissions.includes("payments:manage")));

  const filtered = data.enrollments.filter((enrollment) => {
        if (isStudent && !enrollment.studentIds.includes(profile?.id ?? '')) return false;
        const students = data.students.filter((item) =>
          enrollment.studentIds.includes(item.id),
        );
        const olympiad = data.olympiads.find(
          (item) => item.id === enrollment.olympiadId,
        );
        const haystack =
          `${students.map((item) => `${item.name} ${item.ra} ${item.className} ${formatClassName(item.className)}`).join(" ")} ${olympiad?.acronym}`.toLocaleLowerCase(
            "pt-BR",
          );
        if (
          query.trim() &&
          !haystack.includes(query.trim().toLocaleLowerCase("pt-BR"))
        )
          return false;
        if (status !== "all" && enrollment.status !== status) return false;
        if (payment !== "all" && enrollment.paymentStatus !== payment)
          return false;
        if (olympiadId !== "all" && enrollment.olympiadId !== olympiadId)
          return false;
        if (
          className !== "all" &&
          !students.some((item) => item.className === className)
        )
          return false;
        return true;
      });
  const classes = [...new Set(data.students.map((item) => item.className))].sort(compareClassNames);
  const createOlympiad = data.olympiads.find((item) => item.id === createOlympiadId);
  const eligibleStudents = createOlympiad
    ? data.students.filter((student) => student.active && !student.mustChangePassword && isStudentEligible(createOlympiad, student) && !data.enrollments.some((entry) => entry.olympiadId === createOlympiad.id && entry.status !== "cancelled" && entry.studentIds.includes(student.id)) && (createClass === "all" || student.className === createClass))
    : [];

  const submitCollaboratorEnrollment = async () => {
    if (working) return;
    setWorking(true);
    const result = await createEnrollmentForStudents(createOlympiadId, selectedStudentIds, teamName);
    setWorking(false);
    if (!result.ok) return notify({ type: "error", title: "Inscrição não criada", description: result.error.message });
    const { created, failures } = result.data;
    setSelectedStudentIds(failures.map((item) => item.studentId));
    if (!failures.length) { setCreateOpen(false); setTeamName(''); }
    notify({ type: failures.length ? 'error' : 'success',
      title: failures.length ? `${created.length} inscrição(ões) confirmada(s) no sistema; ${failures.length} pendente(s)` : `${created.length} inscrição(ões) registrada(s)`,
      description: failures.length ? failures.map((item) => `${data.students.find((student) => student.id === item.studentId)?.name ?? 'Estudante'}: ${item.message}`).join('; ') : 'A lista reflete as inscrições persistidas. O pagamento continua conforme cada cobrança.',
    });
  };

  const exportCsv = () => {
    const rows = [
      [
        "Protocolo",
        "Estudante",
        "RA",
        "Turma",
        "Olimpíada",
        "Inscrição",
        "Pagamento",
        "Valor da inscrição (uma vez por inscrição)",
      ],
      ...filtered.flatMap((enrollment) => {
        const members = data.students.filter((item) => enrollment.studentIds.includes(item.id) && (className === 'all' || item.className === className));
        const olympiad = data.olympiads.find(
          (item) => item.id === enrollment.olympiadId,
        );
        const total =
          enrollment.payments?.reduce((sum, item) => sum + item.amount, 0) ?? 0;
        return members.map((student, index) => [
          enrollment.protocol,
          student?.name ?? "",
          student?.ra ?? "",
          formatClassName(student?.className),
          olympiad?.acronym ?? "",
          enrollment.status,
          enrollment.paymentStatus,
          index === 0 ? total.toFixed(2) : "",
        ]);
      }),
    ];
    const blob = new Blob(
      [
        rows
          .map((row) =>
            row
              .map((cell) => `"${String(cell).replaceAll('"', '""')}"`)
              .join(";"),
          )
          .join("\n"),
      ],
      { type: "text/csv;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "inscricoes-e-pagamentos.csv";
    anchor.click();
    URL.revokeObjectURL(url);
    notify({
      type: "success",
      title: "Arquivo CSV gerado",
      description: `${filtered.length} inscrições exportadas.`,
    });
  };

  const setPaymentStatus = async (
    paymentId: string,
    next: EnrollmentPayment["status"],
  ) => {
    if (!active) return;
    setWorking(true);
    const result = await updateEnrollmentPayment(active.id, paymentId, next);
    setWorking(false);
    if (result.ok) {
      setActive(result.data);
      notify({
        type: "success",
        title:
          next === "paid"
            ? "Pagamento confirmado e inscrição atualizada"
            : next === "invalidated"
              ? "Pagamento invalidado"
              : "Pagamento marcado como pendente",
      });
    } else notify({ type: "error", title: result.error.message });
  };
  const cancelActive = async () => {
    if (!active) return;
    setWorking(true);
    const result = await cancelEnrollment(active.id);
    setWorking(false);
    if (result.ok) {
      setActive(result.data);
      notify({ type: "success", title: "Inscrição cancelada" });
    } else notify({ type: "error", title: result.error.message });
  };
  const clear = () => {
    setQuery("");
    setStatus("all");
    setPayment("all");
    setOlympiadId("all");
    setClassName("all");
  };

  return (
    <div className="enrollments-page">
      <PageHeader
        eyebrow={isStudent ? "Acompanhamento" : "Inscrições e financeiro"}
        title={isStudent ? "Minhas inscrições" : "Gestão de inscrições"}
        description={isStudent ? "Consulte o status, pagamentos e datas das suas inscrições." : "Gratuitas são confirmadas automaticamente. Pagas dependem da quitação registrada."}
        actions={<>
          {!isStudent ? <Button variant="secondary" onClick={exportCsv}><Download size={17} /> Exportar</Button> : null}
          {canEnroll ? <Button onClick={() => setCreateOpen(true)}><Plus size={17} /> Inscrever estudantes</Button> : null}
        </>}
      />
      {!isStudent ? <div className="enrollment-rule-banner">
        <Banknote size={20} />
        <div>
          <strong>Aprovação orientada por pagamento</strong>
          <p>
            Não existe aprovação manual para olimpíadas gratuitas. Nas pagas,
            confirmar a parcela libera a inscrição automaticamente.
          </p>
        </div>
      </div> : null}
      <div className="admin-filter-bar">
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder="Buscar estudante, RA, turma ou olimpíada..."
        />
        <select
          value={olympiadId}
          onChange={(e) => setOlympiadId(e.target.value)}
          aria-label="Filtrar por olimpíada"
        >
          <option value="all">Todas as olimpíadas</option>
          {data.olympiads.map((item) => (
            <option key={item.id} value={item.id}>
              {item.acronym} 2026
            </option>
          ))}
        </select>
        <select
          value={className}
          onChange={(e) => setClassName(e.target.value)}
          aria-label="Filtrar por turma"
        >
          <option value="all">Todas as turmas</option>
          {classes.map((item) => (
            <option key={item} value={item}>{formatClassName(item)}</option>
          ))}
        </select>
        <select
          value={payment}
          onChange={(e) => setPayment(e.target.value as PaymentFilter)}
          aria-label="Filtrar por pagamento"
        >
          <option value="all">Todos os pagamentos</option>
          <option value="paid">Pagos</option>
          <option value="pending">Pendentes</option>
          <option value="invalidated">Invalidados</option>
          <option value="not_applicable">Gratuitas</option>
        </select>
      </div>
      <div className="status-summary-tabs">
        {(
          ["all", "pending", "needs_info", "confirmed", "cancelled"] as const
        ).map((item) => (
          <button
            key={item}
            type="button"
            className={status === item ? "active" : ""}
            onClick={() => setStatus(item)}
          >
            {item === "all"
              ? "Todas"
              : item === "pending"
                ? "Aguardando pagamento"
                : item === "needs_info"
                  ? "Com pendência"
                  : item === "confirmed"
                    ? "Confirmadas"
                    : "Canceladas"}{" "}
            <span>
              {item === "all"
                ? data.enrollments.length
                : data.enrollments.filter((entry) => entry.status === item)
                    .length}
            </span>
          </button>
        ))}
      </div>
      {filtered.length ? (
        <div className="responsive-table">
          <table>
            <thead>
              <tr>
                <th>Estudante</th>
                <th>Turma</th>
                <th>Olimpíada</th>
                <th>Data</th>
                <th>Inscrição</th>
                <th>Pagamento</th>
                <th>
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((enrollment) => {
                const student = data.students.find((item) =>
                  enrollment.studentIds.includes(item.id),
                );
                const olympiad = data.olympiads.find(
                  (item) => item.id === enrollment.olympiadId,
                );
                const due = enrollment.payments?.find(
                  (item) => item.status !== "paid",
                );
                return (
                  <tr key={enrollment.id}>
                    <td data-label="Estudante">
                      <div className="person-cell">
                        <span className="avatar">{student?.initials}</span>
                        <div>
                          <strong>{student?.name}</strong>
                          <small>RA {student?.ra}</small>
                        </div>
                      </div>
                    </td>
                    <td data-label="Turma">{formatClassName(student?.className)}</td>
                    <td data-label="Olimpíada">
                      <strong>{olympiad?.acronym} 2026</strong>
                      <small>
                        {olympiad?.costType === "paid" || olympiad?.cost
                          ? "Paga"
                          : "Gratuita"}
                      </small>
                    </td>
                    <td data-label="Data">
                      {new Date(enrollment.requestedAt).toLocaleDateString(
                        "pt-BR",
                      )}
                    </td>
                    <td data-label="Inscrição">
                      <StatusChip status={enrollment.status} />
                      {enrollment.note ? (
                        <small className="row-note">{formatClassCodesInText(enrollment.note)}</small>
                      ) : null}
                    </td>
                    <td data-label="Pagamento">
                      <span
                        className={`payment-chip ${enrollment.paymentStatus}`}
                      >
                        {paymentLabel(enrollment.paymentStatus)}
                      </span>
                      {due ? (
                        <small>
                          {money(due.amount)} · {due.phaseTitle}
                        </small>
                      ) : null}
                    </td>
                    <td>
                      <Button
                        variant={
                          enrollment.paymentStatus === "pending"
                            ? "secondary"
                            : "ghost"
                        }
                        onClick={() => setActive(enrollment)}
                      >
                        {enrollment.paymentStatus === "pending" && canPay ? (
                          <>
                            <Banknote size={16} /> Registrar
                          </>
                        ) : (
                          <>
                            <Eye size={16} /> Ver
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          title="Nenhuma inscrição encontrada"
          description="Ajuste os filtros."
          action={
            <Button variant="secondary" onClick={clear}>
              Limpar filtros
            </Button>
          }
        />
      )}
      <p className="table-footer">
        {filtered.length} de {data.enrollments.length} inscrições
      </p>
      <Dialog
        open={createOpen}
        title="Inscrever estudantes"
        description="Selecione uma olimpíada e apenas estudantes das turmas elegíveis."
        onClose={() => setCreateOpen(false)}
        size="large"
        actions={<><Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancelar</Button><Button loading={working} disabled={!createOlympiadId || !selectedStudentIds.length || (createOlympiad?.format === "Equipe" && !teamName.trim())} onClick={() => void submitCollaboratorEnrollment()}>Confirmar inscrição</Button></>}
      >
        <div className="dialog-form">
          <div className="form-grid two">
            <Field label="Olimpíada"><select value={createOlympiadId} onChange={(event) => { setCreateOlympiadId(event.target.value); setSelectedStudentIds([]); setCreateClass("all"); }}><option value="">Selecione</option>{data.olympiads.filter((item) => item.status === "open" && item.enrollmentMode !== "form").map((item) => <option key={item.id} value={item.id}>{item.acronym} 2026 · {item.format}</option>)}</select></Field>
            <Field label="Turma"><select value={createClass} disabled={!createOlympiad} onChange={(event) => { setCreateClass(event.target.value); setSelectedStudentIds([]); }}><option value="all">Todas as turmas elegíveis</option>{classes.filter((item) => !createOlympiad || createOlympiad.eligibleClasses?.includes(item)).map((item) => <option key={item} value={item}>{formatClassName(item)}</option>)}</select></Field>
          </div>
          {createOlympiad?.format === "Equipe" ? <Field label="Nome da equipe"><input value={teamName} onChange={(event) => setTeamName(event.target.value)} placeholder="Informe o nome da equipe" /></Field> : null}
          {createOlympiad ? <InlineMessage title={createOlympiad.format === "Equipe" ? "Seleção da equipe" : "Inscrição individual em lote"}>{createOlympiad.format === "Equipe" ? `Selecione de ${createOlympiad.teamMin ?? 2} a ${createOlympiad.teamMax ?? createOlympiad.teamMin ?? 2} integrantes. A primeira pessoa será a responsável pela equipe.` : "Cada estudante selecionado receberá uma inscrição individual. Duplicidades e turmas não elegíveis são bloqueadas."}</InlineMessage> : null}
          <div className="participant-picker">
            {eligibleStudents.length ? <>
              {createOlympiad?.format === "Solo" ? <Button variant="secondary" onClick={() => setSelectedStudentIds(selectedStudentIds.length === eligibleStudents.length ? [] : eligibleStudents.map((item) => item.id))}>{selectedStudentIds.length === eligibleStudents.length ? "Limpar seleção" : "Selecionar turma inteira"}</Button> : null}
              {eligibleStudents.map((student) => <label key={student.id}><input type="checkbox" checked={selectedStudentIds.includes(student.id)} onChange={(event) => setSelectedStudentIds((current) => event.target.checked ? [...current, student.id] : current.filter((id) => id !== student.id))} /><span className="avatar">{student.initials}</span><span><strong>{student.name}</strong><small>{formatClassName(student.className)} · RA {student.ra}</small></span></label>)}
            </> : <p className="command-hint">{createOlympiad ? "Nenhum estudante ativo e elegível neste filtro." : "Escolha uma olimpíada para listar os estudantes elegíveis."}</p>}
          </div>
        </div>
      </Dialog>
      <Dialog
        open={Boolean(active)}
        title={active ? `Inscrição ${active.protocol}` : "Inscrição"}
        description="Dados, participantes e parcelas por fase."
        onClose={() => setActive(null)}
        size="large"
        actions={
          active ? (
            <>
              <Button variant="ghost" onClick={() => setActive(null)}>
                Fechar
              </Button>
              {!isStudent && canEnroll && active.status !== "cancelled" ? (
                <Button
                  variant="danger"
                  loading={working}
                  onClick={cancelActive}
                >
                  Cancelar inscrição
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      >
        {active ? (
          <EnrollmentDetail
            enrollment={active}
            working={working}
            onPayment={setPaymentStatus}
            canPay={canPay}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

function EnrollmentDetail({
  enrollment,
  working,
  onPayment,
  canPay,
}: {
  enrollment: Enrollment;
  working: boolean;
  onPayment: (paymentId: string, status: EnrollmentPayment["status"]) => void;
  canPay: boolean;
}) {
  const { data } = useApp();
  if (!data) return null;
  const students = data.students.filter((item) =>
    enrollment.studentIds.includes(item.id),
  );
  const olympiad = data.olympiads.find(
    (item) => item.id === enrollment.olympiadId,
  );
  return (
    <div className="enrollment-drawer-content">
      <div className="detail-data-grid">
        <div>
          <span>Olimpíada</span>
          <strong>{olympiad?.name}</strong>
        </div>
        <div>
          <span>Formato</span>
          <strong>{olympiad?.format}</strong>
        </div>
        <div>
          <span>Solicitada em</span>
          <strong>
            {new Date(enrollment.requestedAt).toLocaleString("pt-BR")}
          </strong>
        </div>
        <div>
          <span>Pagamento</span>
          <strong>{paymentLabel(enrollment.paymentStatus)}</strong>
        </div>
      </div>
      <PrivateEnrollmentAnswers enrollment={enrollment} />
      <h3>Participantes</h3>
      <div className="participant-list">
        {students.map((student) => (
          <article key={student.id}>
            <span className="avatar">{student.initials}</span>
            <div>
              <strong>{student.name}</strong>
              <p>
                {formatClassName(student.className)} · RA {student.ra}
              </p>
            </div>
          </article>
        ))}
      </div>
      {enrollment.payments?.length ? (
        <section className="payment-phase-list">
          <div>
            <h3>Pagamentos por fase</h3>
            <p>Cada alteração é registrada na trilha de atividades.</p>
          </div>
          {enrollment.payments.map((item) => (
            <article key={item.id}>
              <div>
                <strong>{item.phaseTitle}</strong>
                <p>
                  {money(item.amount)}
                  {item.dueDate
                    ? ` · vencimento ${item.dueDate.split("-").reverse().join("/")}`
                    : ""}
                </p>
              </div>
              <span className={`payment-chip ${item.status}`}>
                {paymentLabel(item.status)}
              </span>
              {canPay ? <div className="payment-actions">
                <Button
                  variant={item.status === "paid" ? "primary" : "secondary"}
                  disabled={working}
                  onClick={() => onPayment(item.id, "paid")}
                >
                  Pago
                </Button>
                <Button
                  variant="ghost"
                  disabled={working}
                  onClick={() => onPayment(item.id, "pending")}
                >
                  Pendente
                </Button>
                <Button
                  variant="danger"
                  disabled={working}
                  onClick={() => onPayment(item.id, "invalidated")}
                >
                  Invalidar
                </Button>
              </div> : <small>Seu perfil pode consultar esta parcela, mas não alterar o pagamento.</small>}
            </article>
          ))}
        </section>
      ) : (
        <div className="free-confirmation">
          <CheckIcon />
          <div>
            <strong>Olimpíada gratuita</strong>
            <p>A inscrição elegível foi confirmada automaticamente.</p>
          </div>
        </div>
      )}
      {enrollment.note ? (
        <div className="pending-note">
          <Filter size={17} />
          <div>
            <strong>Pendência identificada</strong>
            <p>{formatClassCodesInText(enrollment.note)}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
function CheckIcon() {
  return <span aria-hidden="true">✓</span>;
}

function PrivateEnrollmentAnswers({ enrollment }: { enrollment: Enrollment }) {
  const { profile } = useApp();
  const [details, setDetails] = useState<EnrollmentAnswers | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  const authorized = Boolean(profile && (enrollment.studentIds.includes(profile.id) || profile.isSystemManager || profile.permissions.includes('students:manage')));
  useEffect(() => {
    let current = true;
    setDetails(null); setState('loading');
    if (authorized) void remoteRepository.getEnrollmentAnswers(enrollment.id).then((result) => {
      if (!current) return;
      if (result.ok) { setDetails(result.data); setState('ready'); } else setState('error');
    });
    return () => { current = false; };
  }, [authorized, enrollment.id, profile?.unitId, attempt]);
  if (!authorized) return null;
  return <section className="private-enrollment-answers" aria-label="Respostas da inscrição">
    <h3>Respostas da inscrição</h3>
    {state === 'loading' ? <p role="status">Carregando respostas...</p> : state === 'error' ? <InlineMessage type="error" title="Não foi possível carregar as respostas"><Button variant="secondary" onClick={() => setAttempt((value) => value + 1)}>Tentar novamente</Button></InlineMessage> : details ? <div className="detail-data-grid">
      <div><span>Turno</span><strong>{details.shift}</strong></div>
      <div><span>Atendimento especializado</span><strong>{details.accessibility}</strong></div>
      <div><span>Observações</span><p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{details.notes || 'Nenhuma observação informada.'}</p></div>
      <div><span>Aceite do regulamento</span><strong>{new Date(details.accepted_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</strong><small>Versão {details.terms_version}</small></div>
    </div> : <p>Nenhuma resposta disponível para seu perfil nesta inscrição. Inscrições anteriores à atualização preservam os dados originais.</p>}
  </section>;
}
